import type {ReactNode} from 'react'
import {useEffect, useRef, useState} from 'react'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {Icon} from 'src/common/Icon'
import type {PolicySection} from '../hooks/usePolicyGenerator'
import {parsePolicyMarkdown} from '../utils/parsePolicyMarkdown'
import {CategoryPreview} from './CategoryPreview'
import type {FormData} from './constants'
import {
  CATEGORY_OPTIONS,
  COMPANY_SIZES,
  COUNTRY_PRESETS,
  INDUSTRIES,
  btn,
  doc,
  docBody,
  docHeader,
  docSection,
  docSectionBody,
  docSectionHead,
  docSectionTitle,
  docTitle,
  eyebrow,
  input,
  skl,
} from './constants'
import {EmptyState} from './EmptyState'
import {FormPanel} from './FormPanel'
import {Editable, MetaPill} from './Primitives'
import {SelectionToolbar} from './SelectionToolbar'

export function TwoPanelLayout({
  step,
  steps,
  total,
  sections,
  generating,
  loadingCategoryIds,
  generated,
  rewriting,
  formData,
  onUpdateSections,
  onCategoriesChange,
  onPrefetchCategory,
  onBack,
  onDownload,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onPushSnapshot,
  error,
}: {
  step: number
  steps: ReactNode[]
  total: number
  sections: PolicySection[]
  loadingCategoryIds: string[]
  generating: boolean
  generated: boolean
  rewriting: boolean
  formData: FormData
  onUpdateSections: (instruction: string, sections: PolicySection[]) => Promise<void>
  onCategoriesChange: (cats: string[]) => void
  onPrefetchCategory: (cat: string) => void
  onBack: () => void
  onDownload: () => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  onPushSnapshot: (sections: PolicySection[]) => void
  error: string | null
}) {
  const cur = formData.country ? (COUNTRY_PRESETS[formData.country]?.cur ?? '€') : '€'
  const [refineText, setRefineText] = useState('')
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
  const [expandedCategoryGroup, setExpandedCategoryGroup] = useState<string | null>(null)

  // Refs to read contentEditable innerHTML on deselect
  const sectionBodyRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const sectionWrapperRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const docBodyRef = useRef<HTMLDivElement>(null)
  const prevSelectedIdRef = useRef<string | null>(null)
  // Stable ref to sections to avoid stale closure in effect
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections

  // Stable ref to onPushSnapshot so the effect below doesn't re-run when prop identity changes
  const onPushSnapshotRef = useRef(onPushSnapshot)
  onPushSnapshotRef.current = onPushSnapshot

  // policyText is raw markdown on first render; after user edits it becomes innerHTML
  const getSectionHTML = (policyText: string) => {
    const html = policyText.trimStart().startsWith('<') ? policyText : parsePolicyMarkdown(policyText)

    return sanitizeHtml(html)
  }

  // Capture manual edits when a section loses selection (selectedSectionId changes away from it)
  useEffect(() => {
    const prevId = prevSelectedIdRef.current
    prevSelectedIdRef.current = selectedSectionId

    if (!prevId || prevId === selectedSectionId) {
      return
    }

    const el = sectionBodyRefs.current.get(prevId)

    if (!el) {
      return
    }

    const currentHTML = el.innerHTML
    const prevSection = sectionsRef.current.find((s) => s.id === prevId)

    if (!prevSection) {
      return
    }

    const expectedHTML = getSectionHTML(prevSection.policyText)

    if (currentHTML !== expectedHTML) {
      const newSections = sectionsRef.current.map((s) => (s.id === prevId ? {...s, policyText: currentHTML} : s))
      onPushSnapshotRef.current(newSections)
    }
  }, [selectedSectionId])

  // Deselect section when user clicks outside any data-epg-section element
  useEffect(() => {
    if (!selectedSectionId) {
      return
    }

    const handler = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-epg-section]')) {
        setSelectedSectionId(null)
      }
    }

    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [selectedSectionId])

  const selectedSection = sections.find((s) => s.id === selectedSectionId) ?? null

  const allCategories = CATEGORY_OPTIONS.flatMap((g) => g.categories)

  const getCategoryLabel = (value: string) => {
    if (value === 'intro') {
      return 'Introduction'
    }

    if (value === 'summary') {
      return 'Summary'
    }

    return allCategories.find((c) => c.value === value)?.label ?? value
  }

  const selectedSectionLabel = selectedSection ? getCategoryLabel(selectedSection.id) : null

  const hasPreamble = sections.some((s) => s.id === 'intro' || s.id === 'summary')
  const showDoc = generated || generating || hasPreamble

  const renderDocBody = () => {
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="15" stroke="rgba(0,0,0,0.12)" strokeWidth="1.5" />
            <path d="M16 10v7" stroke="rgba(0,0,0,0.35)" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="16" cy="21.5" r="1.2" fill="rgba(0,0,0,0.35)" />
          </svg>
          <p className="m-0 max-w-[280px] text-[14px] font-medium text-black/70">Our AI is currently at capacity.</p>
          <p className="m-0 max-w-[280px] text-[13px] text-black/40">Please wait a moment and try generating again.</p>
        </div>
      )
    }

    if (!showDoc && formData.categories.length > 0) {
      return <CategoryPreview categories={formData.categories} />
    }

    if (!showDoc) {
      return <EmptyState />
    }

    const skeletonRows = [
      {w: '38%', h: 12, mt: 0},
      {w: '88%', h: 10, mt: 6},
      {w: '72%', h: 10},
      {w: '80%', h: 10},
      {w: '55%', h: 10, mt: 10},
      {w: '34%', h: 12, mt: 6},
      {w: '92%', h: 10, mt: 6},
      {w: '68%', h: 10},
      {w: '85%', h: 10},
      {w: '50%', h: 10, mt: 10},
      {w: '36%', h: 12, mt: 6},
      {w: '78%', h: 10, mt: 6},
      {w: '63%', h: 10},
      {w: '90%', h: 10},
      {w: '45%', h: 10},
    ]

    const renderSkeleton = (key: string) => (
      <div key={key} className={`${docSection} pl-3`}>
        <div className={docSectionHead}>
          <div className={docSectionTitle}>
            <div className={skl} style={{width: 130, height: 14}} />
          </div>
        </div>
        <div className="mt-2 flex flex-col gap-[5px]">
          {skeletonRows.map((s, i) => (
            <div key={i} className={skl} style={{width: s.w, height: s.h, marginTop: s.mt ?? 0}} />
          ))}
        </div>
      </div>
    )

    const renderSection = (section: PolicySection, i: number) => (
      <div
        key={section.id}
        ref={(el) => {
          if (el) {
            sectionWrapperRefs.current.set(section.id, el)
          } else {
            sectionWrapperRefs.current.delete(section.id)
          }
        }}
        className={`${docSection} relative animate-epg-reveal-up cursor-pointer transition-[background] duration-200 ${sectionItemCls(
          section.id,
        )}`}
        style={{animationDelay: `${i * 60}ms`}}
        data-epg-section
        onClick={() => setSelectedSectionId(section.id)}
        onFocusCapture={() => setSelectedSectionId(section.id)}
      >
        {selectedSectionId === section.id && (
          <div
            className="absolute inset-y-0 left-0 w-0.5 origin-top bg-moss-blue"
            style={{animation: 'epgStripeDown 280ms cubic-bezier(0.25, 0, 0, 1) both'}}
          />
        )}
        <div className={docSectionHead}>
          <div className={docSectionTitle}>
            <Editable value={section.policyText.match(/^#{1,6}\s+(.+)/)?.[1] ?? getCategoryLabel(section.id)} />
          </div>
        </div>
        <div
          ref={(el) => {
            if (el) {
              sectionBodyRefs.current.set(section.id, el)
            } else {
              sectionBodyRefs.current.delete(section.id)
            }
          }}
          className={`${docSectionBody} cursor-text focus:outline-none`}
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{__html: getSectionHTML(section.policyText)}}
        />
      </div>
    )

    const introSection = sections.find((s) => s.id === 'intro')
    const summarySection = sections.find((s) => s.id === 'summary')
    let sectionIndex = 0
    let currentGroup: string | null = null

    const categoryNodes = formData.categories.flatMap((catId) => {
      const group = CATEGORY_OPTIONS.find((g) => g.categories.some((c) => c.value === catId))
      const nodes: ReactNode[] = []

      if (group && group.section !== currentGroup) {
        currentGroup = group.section

        nodes.push(
          <div key={`group-${group.section}`} className="flex items-center gap-3 pb-1 pt-5">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-black/35">
              {group.sectionLabel}
            </span>
            <div className="h-px flex-1 bg-black/[0.06]" />
          </div>,
        )
      }

      if (loadingCategoryIds.includes(catId)) {
        nodes.push(renderSkeleton(catId))
      } else {
        const section = sections.find((s) => s.id === catId)

        if (section) {
          nodes.push(renderSection(section, sectionIndex++))
        }
      }

      return nodes
    })

    return (
      <>
        {loadingCategoryIds.includes('intro') && renderSkeleton('intro')}
        {introSection && renderSection(introSection, sectionIndex++)}
        {categoryNodes}
        {loadingCategoryIds.includes('summary') && renderSkeleton('summary')}
        {summarySection && renderSection(summarySection, sectionIndex++)}
      </>
    )
  }

  const sectionLabel = 'text-[11px] text-black/55 uppercase tracking-[0.06em] font-semibold mb-[10px]'
  const divider = 'h-px bg-black/[0.06] my-4'

  const canonicalOrder = CATEGORY_OPTIONS.flatMap((g) => g.categories.map((c) => c.value))

  const sortByOrder = (cats: string[]) =>
    [...cats].sort((a, b) => canonicalOrder.indexOf(a) - canonicalOrder.indexOf(b))

  const sectionItemCls = (id: string) => {
    if (selectedSectionId === id) {
      return 'pl-3 bg-black/[0.02]'
    }

    return 'hover:bg-black/[0.02] pl-3'
  }

  return (
    <div className="grid gap-6" style={{gridTemplateColumns: '400px 1fr'}}>
      {/* LEFT — controls */}
      <div className="sticky top-6 flex flex-col gap-[14px] self-start">
        {step < total ? (
          <FormPanel step={step} total={total}>
            {steps[step]}
          </FormPanel>
        ) : (
          <div
            className={`flex h-[750px] flex-col rounded-[10px] border border-black/[0.08] bg-moss-white transition-opacity duration-300 ${
              rewriting ? 'pointer-events-none opacity-50' : 'opacity-100'
            }`}
          >
            {/* TOP — sticky */}
            <div className="flex shrink-0 items-center justify-between px-[22px] pt-[22px]">
              <div className="mb-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-black/55">
                Expense categories
              </div>
              <button
                onClick={onBack}
                className="flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[12px] text-black/40 transition-colors duration-150 hover:text-black/70"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M9 2L4 7l5 5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Back to tone
              </button>
            </div>

            <div className={divider} />

            {/* MIDDLE — scrollable categories only */}
            <div className="min-h-0 flex-1 overflow-y-auto px-[22px]">
              <div className="flex flex-col gap-1">
                {CATEGORY_OPTIONS.map((g) => {
                  const selectedCount = g.categories.filter((c) => formData.categories.includes(c.value)).length
                  const isGroupOn = selectedCount > 0
                  const isOpen = expandedCategoryGroup === g.section

                  const toggleGroup = () => {
                    const values = g.categories.map((c) => c.value)

                    if (isGroupOn) {
                      onCategoriesChange(formData.categories.filter((c) => !values.includes(c)))

                      if (isOpen) {
                        setExpandedCategoryGroup(null)
                      }
                    } else {
                      values.forEach((v) => onPrefetchCategory(v))
                      onCategoriesChange(sortByOrder([...new Set([...formData.categories, ...values])]))
                      setExpandedCategoryGroup(g.section)
                    }
                  }

                  const toggleItem = (value: string) => {
                    if (!formData.categories.includes(value)) {
                      onPrefetchCategory(value)
                    }

                    onCategoriesChange(
                      formData.categories.includes(value)
                        ? formData.categories.filter((c) => c !== value)
                        : sortByOrder([...formData.categories, value]),
                    )
                  }

                  return (
                    <div
                      key={g.section}
                      className={`overflow-hidden rounded-lg border transition-colors duration-150 ${
                        isGroupOn ? 'border-green-neon/40 bg-green-neon/[0.06]' : 'border-black/[0.08] bg-moss-white'
                      }`}
                    >
                      <div
                        className="flex cursor-pointer items-center gap-[10px] px-[14px] py-[10px]"
                        onClick={() => setExpandedCategoryGroup((prev) => (prev === g.section ? null : g.section))}
                      >
                        <input
                          type="checkbox"
                          checked={isGroupOn}
                          onChange={toggleGroup}
                          onClick={(e) => e.stopPropagation()}
                          className="size-4 shrink-0 cursor-pointer accent-green-dark"
                        />
                        <span className="flex-1 text-[13px] font-medium">{g.sectionLabel}</span>
                        <span className="shrink-0 text-[11px] text-black/40">
                          {selectedCount}/{g.categories.length}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setExpandedCategoryGroup((prev) => (prev === g.section ? null : g.section))
                          }}
                          className={`flex cursor-pointer border-0 bg-transparent p-[2px_4px] font-[inherit] text-black/35 transition-transform duration-200 ${
                            isOpen ? 'rotate-180' : 'rotate-0'
                          }`}
                        >
                          <svg width="16" height="16" viewBox="0 0 12 12" fill="none">
                            <path
                              d="M2.5 4.5L6 8L9.5 4.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                      <div
                        className="grid transition-[grid-template-rows] duration-200 ease-out"
                        style={{gridTemplateRows: isOpen ? '1fr' : '0fr'}}
                      >
                        <div className="overflow-hidden">
                          <div className="flex flex-col gap-1 pb-[10px] pl-[42px] pr-[14px]">
                            {g.categories.map((cat) => (
                              <label
                                key={cat.value}
                                className="flex cursor-pointer items-center gap-2 py-1 text-[12px] text-black/75"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.categories.includes(cat.value)}
                                  onChange={() => toggleItem(cat.value)}
                                  className="h-[14px] w-[14px] cursor-pointer accent-green-dark"
                                />
                                {cat.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* BOTTOM — sticky */}
            <div className="shrink-0 px-[22px] pb-[22px]">
              <div className={divider} />

              <div className={`${sectionLabel} mb-[10px] flex h-[18px] items-center gap-2`}>
                Refine with AI
                {selectedSectionLabel && (
                  <button
                    className="flex cursor-pointer items-center gap-1.5 rounded-full border-0 bg-moss-blue/10 px-2 py-0 font-semibold normal-case leading-[18px] tracking-normal text-moss-blue hover:bg-moss-blue/20"
                    onClick={() => setSelectedSectionId(null)}
                  >
                    {selectedSectionLabel}
                    <span className="text-[10px] opacity-60">✕</span>
                  </button>
                )}
              </div>
              <div data-epg-section className={`relative flex gap-2 ${selectedSection ? '' : 'group/refine'}`}>
                {!selectedSection && (
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-max max-w-[220px] -translate-x-1/2 rounded-md bg-moss-black px-3 py-2 text-center text-[11px] leading-snug text-moss-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/refine:opacity-100">
                    Click any section in the document to refine it with a prompt
                    <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-moss-black" />
                  </div>
                )}
                <div className="relative flex-1">
                  <input
                    placeholder={
                      selectedSection ? `e.g. Make ${selectedSectionLabel} stricter...` : 'Select a section first'
                    }
                    className={`${input} w-full ${
                      selectedSection ? 'border-moss-blue/50 shadow-[0_0_0_3px_rgba(44,143,255,0.10)]' : ''
                    } ${refineText ? 'pr-7' : ''}`}
                    style={{fontSize: 13, padding: '10px 12px'}}
                    value={refineText}
                    disabled={!selectedSection}
                    onChange={(e) => setRefineText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && !refineText && selectedSection) {
                        e.preventDefault()
                        setRefineText(`Make ${selectedSectionLabel} stricter`)
                      }

                      if (e.key === 'Enter' && refineText.trim() && selectedSection) {
                        void onUpdateSections(refineText.trim(), [selectedSection])
                        setRefineText('')
                      }
                    }}
                  />
                  {refineText && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded border-0 bg-transparent p-0.5 text-black/30 hover:text-black/60"
                      onClick={() => setRefineText('')}
                      tabIndex={-1}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  className="flex shrink-0 cursor-pointer items-center rounded-lg border-0 bg-moss-black px-3 py-[10px] text-moss-white disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={!refineText.trim() || !selectedSection}
                  onClick={() => {
                    if (refineText.trim() && selectedSection) {
                      void onUpdateSections(refineText.trim(), [selectedSection])
                      setRefineText('')
                    }
                  }}
                >
                  <Icon name="right-arrow" className="size-4" />
                </button>
              </div>

              <div className={divider} />

              <button
                className={`${btn} w-full justify-center !px-7`}
                onClick={onDownload}
                disabled={sections.length === 0}
              >
                <Icon name="download" className="size-4" /> Download PDF
              </button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT — document */}
      <div className={`${doc} relative flex h-[750px] flex-col`}>
        {generating && (
          <>
            <style>{`@keyframes epgDotPulse{0%,100%{transform:scale(.75);opacity:.7;box-shadow:0 0 0 0 rgba(180,255,68,0)}50%{transform:scale(1);opacity:1;box-shadow:0 0 0 5px rgba(180,255,68,0.2)}}`}</style>
            <span
              className="pointer-events-none absolute right-4 top-4 z-10 flex items-center gap-[6px] rounded-full bg-moss-black px-3 py-[6px] text-[11px] font-bold uppercase tracking-[0.06em] text-green-neon"
              style={{animation: 'epgRevealModal 200ms cubic-bezier(0.25,0,0,1) both'}}
            >
              <span
                className="h-[6px] w-[6px] rounded-full bg-green-neon"
                style={{animation: 'epgDotPulse 1.2s ease-in-out infinite'}}
              />
              Generating
            </span>
          </>
        )}
        <div className={`${docHeader} relative shrink-0`}>
          <div className="flex items-center gap-2">
            <span className={`${eyebrow} text-moss-blue`}>Spend policy</span>
            {generated && !rewriting && (
              <span className="flex flex-1 items-center gap-1 text-[10px] text-black/40">
                <Icon name="pencil" className="h-[10px] w-[10px]" /> Click any text to edit
              </span>
            )}
            {generated && !generating && !rewriting && (canUndo || canRedo) && (
              <div className="flex items-center gap-0.5">
                <button
                  onClick={onUndo}
                  disabled={!canUndo}
                  title="Undo"
                  className={`flex items-center rounded border-0 bg-transparent p-[6px] ${
                    canUndo ? 'cursor-pointer text-moss-black' : 'cursor-default text-black/20'
                  }`}
                >
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M3 6.5h6a2.5 2.5 0 0 1 0 5H6.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.5 4.5L3 6.5l2.5 2"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  onClick={onRedo}
                  disabled={!canRedo}
                  title="Redo"
                  className={`flex items-center rounded border-0 bg-transparent p-[6px] ${
                    canRedo ? 'cursor-pointer text-moss-black' : 'cursor-default text-black/20'
                  }`}
                >
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M11 6.5H5a2.5 2.5 0 0 0 0 5h2.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.5 4.5L11 6.5 8.5 8.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
            {rewriting && (
              <span className="absolute right-5 top-4 flex animate-epg-reveal-modal-200 items-center gap-[6px] rounded-full bg-moss-black px-3 py-[6px] text-[11px] font-bold uppercase tracking-[0.06em] text-green-neon">
                <span className="h-[6px] w-[6px] animate-epg-neon-pulse rounded-full bg-green-neon" />
                Rewriting
              </span>
            )}
          </div>
          <div className={`${docTitle} mt-[10px]`}>{formData.companyName || 'Your Company'}</div>
          <div className="mt-[10px] flex flex-wrap gap-[6px]">
            <MetaPill
              filled={!!formData.industry}
              label={INDUSTRIES.find((i) => i.value === formData.industry)?.label || 'Select industry'}
            />
            <MetaPill
              filled={!!formData.companySize}
              label={COMPANY_SIZES.find((s) => s.value === formData.companySize)?.label || 'Select size'}
            />
            <MetaPill
              filled={!!formData.country}
              label={formData.country ? `${COUNTRY_PRESETS[formData.country]?.label} (${cur})` : 'Select country'}
            />
          </div>
        </div>
        <div ref={docBodyRef} className={`${docBody} flex-1 overflow-y-auto`}>
          {renderDocBody()}
        </div>
      </div>
      {generated && <SelectionToolbar />}
    </div>
  )
}
